import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/cn';

import { useDialogElement } from './useDialogElement';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Rendered in a footer row; usually a pair of Buttons. */
  footer?: ReactNode;
  /** Set false for destructive confirmations that must be answered explicitly. */
  closeOnBackdrop?: boolean;
  showCloseButton?: boolean;
  /**
   * `alertdialog` for a destructive confirmation.
   *
   * The distinction is not cosmetic: `alertdialog` tells a screen reader this is
   * an interruption requiring a decision, so the description is announced with
   * the dialog rather than only on focus. A native `<dialog>` is a plain
   * `dialog` by default, which under-states a "delete this permanently" prompt.
   */
  role?: 'dialog' | 'alertdialog';
  className?: string;
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="size-5">
      <path
        d="m6 6 12 12M18 6 6 18"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  closeOnBackdrop = true,
  showCloseButton = true,
  role,
  className
}: ModalProps) {
  const { t } = useTranslation();
  const dialogRef = useDialogElement(open, onClose);
  const id = useId();

  return (
    <dialog
      ref={dialogRef}
      role={role}
      aria-labelledby={title !== undefined ? `${id}-title` : undefined}
      aria-describedby={description !== undefined ? `${id}-desc` : undefined}
      onClick={(event) => {
        // A click landing on the <dialog> itself (not the panel inside it) is a
        // backdrop click — the ::backdrop pseudo-element is not an event target.
        if (closeOnBackdrop && event.target === dialogRef.current) onClose();
      }}
      className={cn(
        'w-[calc(100vw-2rem)] max-w-md rounded-2xl border-0 bg-[var(--surface-elevated)] p-0',
        'text-[var(--text-primary)] shadow-lg',
        'backdrop:bg-[var(--overlay)] backdrop:backdrop-blur-sm',
        'm-auto',
        className
      )}
    >
      <div className="flex flex-col gap-3 p-5">
        {(title !== undefined || showCloseButton) && (
          <div className="flex items-start justify-between gap-3">
            {title !== undefined && (
              <h2 id={`${id}-title`} className="text-title font-semibold">
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                aria-label={t('common.close')}
                className="-mr-1 -mt-1 ml-auto grid size-9 shrink-0 place-items-center rounded-pill text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]"
              >
                <CloseIcon />
              </button>
            )}
          </div>
        )}

        {description !== undefined && (
          <p id={`${id}-desc`} className="text-body-sm text-[var(--text-muted)]">
            {description}
          </p>
        )}

        {children}

        {footer !== undefined && <div className="mt-2 flex gap-3">{footer}</div>}
      </div>
    </dialog>
  );
}
